import {useEffect, useMemo, useState} from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Eye,
  X,
} from 'lucide-react';
import {Card, CardContent} from '@/components/ui/Card';
import {Button} from '@/components/ui/Button';
import {Input} from '@/components/ui/Input';
import {Checkbox} from '@/components/ui/Checkbox';
import {Badge} from '@/components/ui/Badge';
import {Skeleton} from '@/components/ui/Skeleton';
import {StatusPill} from '@/components/app/StatusPill';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {canLookUpByNumber, useTransactions} from '@/lib/api/transactions';
import {formatDate} from '@/lib/utils/format';
import {getDocTypeLabel} from '@/lib/utils/edi-codes';
import {cn} from '@/lib/utils/cn';
import type {
  TransactionDirection,
  TransactionSearchMode,
  TransactionStatus,
  TransactionType,
} from '@/lib/api/types';
import {DocumentStatus} from '@/lib/api/types';
import {useCurrentProvider} from '@/lib/providers/useCurrentProvider';
import {TransactionDetailSheet} from './TransactionDetailSheet';
import {GroupSearchSelect} from './GroupSearchSelect';

const STATUS_OPTIONS: Array<{value: TransactionStatus | 'all'; label: string}> =
  [
    {value: 'all', label: 'All statuses'},
    {value: DocumentStatus.PENDING, label: 'Pending'},
    {value: DocumentStatus.PROCESSING, label: 'Processing'},
    {value: DocumentStatus.COMPLETED, label: 'Completed'},
    {value: DocumentStatus.FAILED, label: 'Failed'},
    {value: DocumentStatus.RETRY, label: 'Retry'},
    {value: DocumentStatus.MISSING_DATA, label: 'Missing data'},
    {value: DocumentStatus.TRANSFER_FAILED, label: 'Transfer failed'},
    {value: DocumentStatus.IMS_UPDATE_FAILED, label: 'IMS update failed'},
    {value: DocumentStatus.SKIPPED, label: 'Skipped'},
    {value: DocumentStatus.CANCELED, label: 'Canceled'},
    {value: DocumentStatus.PREVIOUSLY_PROCESSED, label: 'Previously processed'},
  ];

// Only invoices and credits are surfaced today. Orders haven't shipped, and the
// "All" view confused stakeholders when the two streams returned different
// shapes — stick with the two we support.
const TYPE_TABS: Array<{id: TransactionType; label: string}> = [
  {id: 'invoice', label: 'Invoices'},
  {id: 'credit', label: 'Credits'},
];

// A status filter and a number lookup hit different backend access patterns and
// can't be combined, so the two are offered as an explicit either/or rather than
// as filters that appear to compose.
const SEARCH_MODES: Array<{id: TransactionSearchMode; label: string}> = [
  {id: 'status', label: 'Filter by status'},
  {id: 'name', label: 'Find by number'},
];

export function TransactionsPage() {
  const provider = useCurrentProvider();
  // Document ids are composite — `150961#782#WEBGRP` is invoice number 150961,
  // location 782, group WEBGRP. In status mode these are independent filters;
  // in name mode all three are required and compose into the lookup key.
  const [number, setNumber] = useState('');
  const [location, setLocation] = useState('');
  // null = no group filter.
  const [groupDb, setGroupDb] = useState<string | null>(null);
  const [typeTab, setTypeTab] = useState<TransactionType>('invoice');
  const [status, setStatus] = useState<TransactionStatus | 'all'>('all');
  const [mode, setMode] = useState<TransactionSearchMode>('status');
  // Direction is fixed to outbound for now — only outbound traffic exists in
  // this environment. Surface the filter again once inbound data lands.
  const direction: TransactionDirection = 'outbound';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // DynamoDB pagination is cursor-based (forward-only). Track the cursor for
  // each visited page so users can step back without re-walking from page 1.
  // pageCursors[0] is always undefined (page 1 = no exclusiveStartKey).
  const PAGE_SIZE = 25;
  const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([
    undefined,
  ]);
  const [pageIndex, setPageIndex] = useState(0);

  // Reset paging whenever filters that change the underlying result set move.
  useEffect(() => {
    setPageCursors([undefined]);
    setPageIndex(0);
  }, [
    mode,
    number,
    location,
    groupDb,
    typeTab,
    status,
    direction,
    provider.apiName,
  ]);

  // Clear the input that belongs to the mode being left, so a value the user
  // can no longer see can't silently narrow the new query.
  const switchMode = (next: TransactionSearchMode) => {
    if (next === mode) return;
    setMode(next);
    if (next === 'name') setStatus('all');
    else setNumber('');
  };

  const query = useMemo(
    () => ({
      mode,
      number: number || undefined,
      location: location || undefined,
      groupDb: groupDb ?? undefined,
      type: typeTab,
      status: mode === 'status' && status !== 'all' ? status : undefined,
      direction,
      partnerId: provider.apiName,
      limit: PAGE_SIZE,
      cursor: pageCursors[pageIndex],
    }),
    [
      mode,
      number,
      location,
      groupDb,
      typeTab,
      status,
      direction,
      provider.apiName,
      pageCursors,
      pageIndex,
    ]
  );

  const isNameMode = mode === 'name';
  // In name mode the query stays parked until all three id segments are filled.
  const lookupReady = !isNameMode || canLookUpByNumber(query);

  const {data, isLoading, isError} = useTransactions(query);
  const rows = data?.items ?? [];

  // Composite row identity — DynamoDB partition + sort key. Two rows can share
  // the same `id` across different `modified` revisions, so we need both for
  // React reconciliation and selection.
  const rowKey = (t: {id: string; modified: string}) => `${t.id}#${t.modified}`;

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(rowKey)));
  };
  const toggleOne = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Transactions
          </h1>
          <p className="text-sm text-muted-foreground">
            {provider.label} documents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex items-center gap-1 border-b">
        {TYPE_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTypeTab(t.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              typeTab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search mode — status filter OR number lookup, never both */}
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="radiogroup"
          aria-label="Search mode"
          className="inline-flex items-center rounded-md border bg-muted/30 p-0.5"
        >
          {SEARCH_MODES.map(m => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={mode === m.id}
              onClick={() => switchMode(m.id)}
              className={cn(
                'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                mode === m.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          {isNameMode
            ? 'Exact match — number, location and group are all required.'
            : 'Browse a paged list of documents.'}
        </span>
      </div>

      {/* Filters row */}
      {isNameMode ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {typeTab === 'credit' ? 'Credit number' : 'Invoice number'} *
            </span>
            <Input
              value={number}
              onChange={e => setNumber(e.target.value)}
              placeholder={
                typeTab === 'credit' ? 'Credit number' : 'Invoice number'
              }
              className="w-[200px]"
              aria-required="true"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              Location *
            </span>
            <Input
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Location"
              className="w-[140px]"
              aria-required="true"
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              Group *
            </span>
            <GroupSearchSelect
              value={groupDb}
              onChange={setGroupDb}
              className="w-[280px]"
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="Location"
            className="w-[140px]"
            aria-label="Location"
          />

          <GroupSearchSelect
            value={groupDb}
            onChange={setGroupDb}
            className="w-[280px]"
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <span className="text-xs text-muted-foreground">Status:</span>
                <span>
                  {STATUS_OPTIONS.find(o => o.value === status)?.label ?? 'All'}
                </span>
                <ChevronsUpDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="bottom"
              sideOffset={4}
              collisionPadding={8}
              className="w-56 max-h-[60vh] overflow-y-auto"
            >
              {STATUS_OPTIONS.map(o => (
                <DropdownMenuItem
                  key={o.value}
                  onSelect={() => setStatus(o.value as typeof status)}
                  className="justify-between"
                >
                  <span>{o.label}</span>
                  {status === o.value && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
          >
            Date{' '}
            {sortDir === 'desc' ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <ArrowUp className="h-3 w-3" />
            )}
          </Button>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="flex items-center gap-3 py-3">
            <Badge>{selected.size} selected</Badge>
            <span className="text-sm text-muted-foreground flex-1">
              Apply an action to the selected transactions.
            </span>
            <Button size="sm" variant="outline">
              <Download className="h-3.5 w-3.5" />
              Download
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setSelected(new Set())}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="glass overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {typeTab === 'credit' ? 'Credit #' : 'Invoice #'}
                </th>
                <th className="px-4 py-3 text-left font-medium">Location</th>
                <th className="px-4 py-3 text-left font-medium">Group</th>
                <th className="px-4 py-3 text-left font-medium">Direction</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Doc type</th>
                <th className="px-4 py-3 text-left font-medium">Partner</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({length: 6}).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3" colSpan={11}>
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-16 text-center text-muted-foreground"
                  >
                    {/* A failed request must not read as an empty result —
                        "not found" is a claim about the data, not the call. */}
                    {isError
                      ? 'Could not load documents. Check the connection and try again.'
                      : !lookupReady
                        ? 'Enter number, location and group to look up a document.'
                        : isNameMode
                          ? `No document found for ${number.trim()}#${location.trim()}#${groupDb ?? ''}.`
                          : 'No transactions match these filters.'}
                  </td>
                </tr>
              ) : (
                rows.map(tx => {
                  const key = rowKey(tx);
                  return (
                    <tr
                      key={key}
                      className="group hover:bg-muted/40 transition-colors cursor-pointer"
                      onClick={() => setOpenId(tx.id)}
                    >
                      <td
                        className="px-4 py-3"
                        onClick={e => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selected.has(key)}
                          onCheckedChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium tabular-nums">
                        {tx.number}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">
                        {tx.location || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {tx.groupDb ? (
                          <Badge variant="outline" className="font-mono">
                            {tx.groupDb}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="muted" className="capitalize">
                          {tx.direction}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={tx.status} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground tabular-nums">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          <Badge variant="outline" className="font-mono">
                            {tx.docType}
                          </Badge>
                          <span className="text-muted-foreground text-xs">
                            {getDocTypeLabel(tx.docType)}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3">{tx.tradingPartner}</td>
                      <td className="px-4 py-3 capitalize text-muted-foreground">
                        {tx.type}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Eye className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity inline" />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data && !isNameMode && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-muted-foreground border-t">
            <span>
              Page {pageIndex + 1} · showing {rows.length} transaction
              {rows.length === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex === 0 || isLoading}
                onClick={() => setPageIndex(i => Math.max(0, i - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.hasMore || isLoading}
                onClick={() => {
                  if (!data.nextCursor) return;
                  setPageCursors(prev => {
                    if (prev[pageIndex + 1] === data.nextCursor) return prev;
                    const next = prev.slice(0, pageIndex + 1);
                    next.push(data.nextCursor);
                    return next;
                  });
                  setPageIndex(i => i + 1);
                }}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <TransactionDetailSheet
        transactionId={openId}
        transactionType={typeTab}
        onOpenChange={open => !open && setOpenId(null)}
      />
    </div>
  );
}
